<template>
  <v-row justify="center">
    <v-col style="max-width: 540px !important">
      <v-row no-gutters align="center" justify="start" v-if="loading">
        <v-col cols="12">
          <v-boilerplate type="image, article" />
          <v-skeleton-loader />
        </v-col>
      </v-row>
      <v-card min-width="350" v-else>
        <v-card-title class="py-4 text-center" v-if="context.account && context.account.logo">
          <v-spacer />
          <v-sheet rounded outlined>
            <v-responsive class="align-center" :aspect-ratio="1/1">
              <v-sheet class="px-1" width="100">
                <v-img :src="context.account.logo" width="100%" />
              </v-sheet>
            </v-responsive>
          </v-sheet>
          <v-spacer />
        </v-card-title>
        <v-card-subtitle class="py-4">
          <v-row>
            <v-col class="text-center" cols="12">
              <span class="d-block body-2 text--secondary font-weight-medium">{{ context.account && context.account.name }}</span>
            </v-col>
          </v-row>
        </v-card-subtitle>
        <v-divider />
        <v-card-text class="px-12 py-6 background">
          <!-- no eligible card gateway for the account/currency -->
          <v-row class="px-4" no-gutters v-if="!context.has_gateway">
            <v-col cols="12" class="py-6 text-center">
              <v-icon class="d-block mb-3" size="40" color="red">mdi-alert</v-icon>
              <span class="d-block body-1 font-weight-medium">No hay medio de pago disponible</span>
              <span class="d-block body-2 text--secondary mt-1">No es posible registrar una tarjeta en este momento. Contacta a {{ context.account && context.account.name }}.</span>
            </v-col>
          </v-row>

          <!-- heading -->
          <v-row class="px-4" no-gutters v-else>
            <v-col cols="12" class="text-center">
              <span class="d-block mb-2 body-1 font-weight-medium">
                <template v-if="isUpdate">Actualiza tu tarjeta</template>
                <template v-else>Registra tu tarjeta</template>
              </span>
              <span class="d-block body-2 text--primary mt-1">
                <template v-if="isUpdate && context.current_card">
                  Reemplázala para que el próximo cobro de tu suscripción a <b>{{ context.account.name }}</b> no se interrumpa
                </template>
                <template v-else>
                  Guarda tu método de pago para mantener activa tu suscripción a <b>{{ context.account.name }}</b>
                </template>
              </span>
            </v-col>
          </v-row>
          <!-- end heading -->

          <!-- current card -->
          <v-row v-if="isUpdate && context.current_card" no-gutters>
            <v-col cols="12" class="text-center">
              <v-sheet rounded outlined class="d-flex pa-4 mt-4 align-center justify-space-between">
                <span>Tarjeta actual</span>
                <div class="d-flex align-center">
                  <img :src="require(`@/assets/appAsset-cardbrand--${context.current_card.brand}.svg`)" :alt="context.current_card.brand" height="22" />
                  <span class="ml-3 text--secondary font-weight-medium">•••• •••• •••• {{ context.current_card.last4 }}</span>
                </div>
              </v-sheet>
            </v-col>
          </v-row>
          <!-- end current card -->

          <!-- flow=none (e.g. Stripe): inline tokenization -->
          <v-row v-if="context.flow === 'none'">
            <v-col cols="12" class="text-center">
              <span class="d-block caption text--secondary">
                Ingresa los datos de tu nueva tarjeta
              </span>
            </v-col>
            <v-col cols="12">
              <template v-if="context.gateway === 'stripe'">
                <gateway-stripe ref="stripe" :test-mode="context.test_mode" />
              </template>
            </v-col>
            <v-col cols="12">
              <v-btn @click="storeSource" block :color="context.account.color_background" dark height="40" :loading="loaderStore">{{ actionLabel }}</v-btn>
            </v-col>
          </v-row>
          <!-- end flow=none -->

          <!-- flow=redirect (e.g. Transbank/OneClick): redirect to the gateway -->
          <v-row v-else-if="context.flow === 'redirect'">
            <v-col cols="12" class="text-center">
              <v-sheet rounded outlined class="d-flex text-left pa-4 justify-space-between">
                <v-icon :color="context.account.color_background">mdi-shield-check</v-icon>
                <span class="ml-2">Para proteger tus datos, completarás el registro en el <b>sitio seguro de {{ gatewayName }}</b>. Nunca compartirás los datos de tu tarjeta con nosotros.</span>
              </v-sheet>
            </v-col>
            <v-col cols="12">
              <v-btn @click="redirectToGateway" block :color="context.account.color_background" dark height="40" :loading="loaderStore">Continuar al sitio seguro</v-btn>
            </v-col>
          </v-row>
          <!-- end flow=redirect -->

          <!-- security info -->
          <v-row class="mt-6" no-gutters>
            <v-col cols="12" class="pb-4 text-center">
              <v-icon color="secondary" size="20">mdi-shield-outline</v-icon>
              <span class="d-block caption text--primary">Conexión cifrada. No almacenamos los datos de tu tarjeta en este sitio.</span>
            </v-col>
            <v-col cols="12" class="pb-4 text-center">
              <v-chip class="mx-1" outlined small><v-icon left x-small>mdi-lock-outline</v-icon>SSL</v-chip>
              <v-chip class="mx-1" outlined small>PCI DSS</v-chip>
              <v-chip class="mx-1" outlined small>Pago seguro</v-chip>
            </v-col>
            <v-col cols="12" class="text-center">
              <span class="d-block caption text--primary">¿Necesitas ayuda? Escríbenos a soporte@empresa.com</span>
            </v-col>
          </v-row>
          <!-- end security info -->

        </v-card-text>
      </v-card>

      <piriod-sign />

    </v-col>
  </v-row>
</template>
<script>
// mixins
import GenericViewMixin from '@/mixins/GenericViewMixin'

// collections
import gateways from '@/collections/gateways'

// backend
import api from '@/backend/api'

// components
import GatewayStripe from '@/components/GatewayStripe'
import PiriodSign from '@/components/PiriodSign'

export default {
  inject: ['theme'],
  components: {
    GatewayStripe,
    PiriodSign,
    VBoilerplate: {
      functional: true,
      render (h, { data, props, children }) {
        return h('v-skeleton-loader', {
          ...data,
          props: {
            boilerplate: true,
            ...props
          }
        }, children)
      }
    }
  },
  mixins: [
    GenericViewMixin
  ],
  data: () => ({
    // local context (not the shared store) so a CREATE response can't clobber it
    context: {},
    gateways: gateways,
    loading: true,
    loaderStore: false
  }),
  computed: {
    // "update" wording is driven by the link, not by backend autodetection
    isUpdate () {
      return this.$route.query.update === 'true'
    },
    actionLabel () {
      return this.isUpdate ? 'Actualizar tarjeta' : 'Registrar tarjeta'
    },
    gatewayName () {
      return this.gateways.find(g => g.id === this.context.gateway)?.name || 'tu pasarela de pago'
    }
  },
  created () {
    // resolve context via the customer publishable id:
    // GET /publishable/sources/context/{token}/?gateway=...
    const query = {}
    if (this.$route.query.gateway) {
      query.gateway = this.$route.query.gateway
    }

    api.get(`publishable/sources/context/${this.$route.params.token}`, query)
    .then((response) => {
      this.context = response.data
      // seed the store so GatewayStripe (which reads state.payments.sourcesDetail.merchant)
      // can initialize; the template binds to local `context`, not the store, so a later
      // CREATE response overwriting sourcesDetail can't blank out this view
      this.$store.commit('payments/SET_DETAIL', {
        response: { data: response.data, config: { method: 'get' } },
        resource: 'publishable/sources'
      })
    })
    .catch((error) => {
      if (error.response && error.response.status === 404) {
        this.$router.push({ name: 'PageExpired' })
      }
    })
    .finally(() => {
      this.loading = false
    })
  },
  methods: {
    buildPayload (extra = {}) {
      const payload = { customer_token: this.$route.params.token, ...extra }
      if (this.$route.query.gateway) {
        payload.gateway = this.$route.query.gateway
      }
      return payload
    },
    async storeSource () {
      this.loaderStore = true

      const token = await this.$refs[this.context.gateway].send()

      // create + inscribe the reusable source for this customer
      try {
        await api.create('publishable/sources', this.buildPayload({ token }))
      } catch (e) {
        this.loaderStore = false
        if (this.context.return_url) {
          window.location.href = this.context.return_url + `?error=${encodeURIComponent(e.message)}`
        } else {
          this.$router.push({ name: 'SourceStoreError' })
        }
        return
      }

      if (this.context.return_url) {
        window.location.href = this.context.return_url + `?success=true`
      } else {
        this.$router.push({ name: 'SourceStoreSuccess' })
      }
    },
    async redirectToGateway () {
      this.loaderStore = true

      // create the reusable source first, then hand off to the server-driven
      // redirect inscription flow: /sources/{id}/authorize/
      let response
      try {
        response = await api.create('publishable/sources', this.buildPayload())
      } catch (e) {
        this.loaderStore = false
        if (this.context.return_url) {
          window.location.href = this.context.return_url + `?error=${encodeURIComponent(e.message)}`
        } else {
          this.$router.push({ name: 'SourceStoreError' })
        }
        return
      }

      // keep the loader on while the browser navigates to the gateway
      const apiBase = process.env.VUE_APP_API_BASE
      window.location.href = `${apiBase}/sources/${response.data.id}/authorize/`
    }
  }
}
</script>
